import * as React from 'react';

import type { AudienceDelta, Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useAudienceLearnerEditor } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearnerEditor';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import useLearnerList from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { MOCK_ORG_LEARNERS } from 'bundles/enterprise-admin-messaging-v2/utils/mockLearners';

/**
 * What to persist when the admin saves, discriminated by entry point:
 *  - `filter`: a removals-only delta against the server cohort (auto-generate
 *    flow); the (future) BE filter-regeneration API will consume it.
 *  - `list`: the in-memory working copy (manual flow).
 */
export type LearnerListSaveResult = { kind: 'filter'; delta: AudienceDelta } | { kind: 'list'; learners: Learner[] };

type Params = {
  /** Cohort the editor opens with — the in-memory working copy starts here. */
  initialLearners: Learner[];
  /**
   * Filter-backed entry point: the pool is fetched server-side from this ready
   * `{ orgId, filter }` audience and edits are tracked as a delta. Absent for
   * the manual flow.
   */
  audience?: LearnerListAudience;
  /** Delta to seed the editor with when re-opening (filter flow). */
  initialDelta?: AudienceDelta;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  /** Total rows matched by the current query (across all pages). */
  filteredCount: number;
  loading: boolean;
  /** True when the (server) page fetch failed. */
  error: boolean;
  /** Re-include / add a learner (by row). */
  addLearner: (learner: Learner) => void;
  /** Remove a learner from the selection (by id). */
  removeLearner: (id: string) => void;
  /** The payload to persist on Save, discriminated by entry point. */
  save: LearnerListSaveResult;
};

/**
 * Single source of truth for *how each entry point* sources, searches, and edits
 * the learner list behind the "Add or remove learners" screen. The screen stays
 * presentational — it renders `pageRows` and calls `addLearner` / `removeLearner`
 * without knowing whether the data is server-fetched or in-memory.
 *
 * Two entry points exist today; add a branch here for new ones:
 *  - **Filter:** `audience` present. The cohort is fetched page-by-page from
 *    the gateway and edits are a removals-only delta.
 *  - **Manual:** no audience. The working copy is an in-memory list; `+ Add`
 *    pulls from the org pool minus the current cohort.
 */
export const useLearnerListEntryPoint = ({ initialLearners, audience, initialDelta }: Params): Result => {
  const isServer = Boolean(audience);

  // --- Manual (in-memory) entry point ---
  const [editedList, setEditedList] = React.useState<Learner[]>(initialLearners);
  // "All learners" minus the current cohort. Any overlap is treated as part of
  // the cohort — a learner must never show both a trash icon and a +Add button.
  const availablePool = React.useMemo(() => {
    const cohortIds = new Set(editedList.map((learner) => learner.id));
    return MOCK_ORG_LEARNERS.filter((learner) => !cohortIds.has(learner.id));
  }, [editedList]);
  const clientEditor = useLearnerList({ list: editedList, availablePool });

  // --- Filter (server-backed) entry point ---
  // Both hooks run unconditionally (Rules of Hooks); the server editor skips its
  // query when there's no audience, so the unused branch stays cheap.
  const serverEditor = useAudienceLearnerEditor({ audience, initialDelta });

  const addLearner = React.useCallback(
    (learner: Learner) => {
      if (isServer) {
        serverEditor.addLearner(learner.id);
      } else {
        setEditedList((previous) =>
          previous.some((existing) => existing.id === learner.id) ? previous : [...previous, learner]
        );
      }
    },
    [isServer, serverEditor]
  );

  const removeLearner = React.useCallback(
    (id: string) => {
      if (isServer) {
        serverEditor.removeLearner(id);
      } else {
        setEditedList((previous) => previous.filter((learner) => learner.id !== id));
      }
    },
    [isServer, serverEditor]
  );

  // The active source supplies the common list/search/pagination surface; only
  // the server editor reports a real loading state (the in-memory list is sync).
  const active = isServer ? serverEditor : clientEditor;

  const save: LearnerListSaveResult = isServer
    ? { kind: 'filter', delta: serverEditor.delta }
    : { kind: 'list', learners: editedList };

  return {
    searchQuery: active.searchQuery,
    setSearchQuery: active.setSearchQuery,
    page: active.page,
    setPage: active.setPage,
    pageCount: active.pageCount,
    pageRows: active.pageRows,
    filteredCount: active.filteredCount,
    loading: isServer ? serverEditor.loading : false,
    error: isServer ? serverEditor.error : false,
    addLearner,
    removeLearner,
    save,
  };
};

export default useLearnerListEntryPoint;
